// src/components/tables/DriversTable.js
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    TableSortLabel,  // 加入這行
    Box,
    Typography,
    InputAdornment,
    IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import PropTypes from 'prop-types';
import { useState } from 'react';

import DriverEvaluationDialog from '../evaluation/DriverEvaluationDialog';


const DriversTable = ({ drivers }) => {
    const [orderBy, setOrderBy] = useState('total_score');
    const [order, setOrder] = useState('desc');
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [selectedVehicleId, setSelectedVehicleId] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    // 取得當前年月
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    // 處理查看評鑑按鈕點擊
    const handleEvaluationClick = (vehicleId) => {
        setSelectedVehicleId(vehicleId);
        setDialogOpen(true);
    };


    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0); // 重設分頁到第一頁
    };
    // 搜尋過濾邏輯
    const filteredDrivers = drivers.filter(driver =>
        driver.identifier.toLowerCase().includes(searchQuery.toLowerCase()) ||
        driver.driver_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        driver.score_level.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // 排序邏輯
    const sortedDrivers = [...filteredDrivers].sort((a, b) => {
        if (order === 'asc') {
            return a[orderBy] < b[orderBy] ? -1 : 1;
        }
        return b[orderBy] < a[orderBy] ? -1 : 1;
    });

    // 分頁邏輯
    const paginatedDrivers = sortedDrivers.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Paper sx={{ width: '100%', mb: 2 }}>
            {/* 搜尋框 */}
            <Box sx={{ p: 2 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="搜尋車輛編號、駕駛姓名或評分等級..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <TableContainer>
                <Table sx={{ minWidth: 750 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">
                                <TableSortLabel
                                    active={orderBy === 'identifier'}
                                    direction={orderBy === 'identifier' ? order : 'asc'}
                                    onClick={() => handleRequestSort('identifier')}
                                >
                                    車輛編號
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center">
                                <TableSortLabel
                                    active={orderBy === 'driver_name'}
                                    direction={orderBy === 'driver_name' ? order : 'asc'}
                                    onClick={() => handleRequestSort('driver_name')}
                                >
                                    營運公司
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center">
                                <TableSortLabel
                                    active={orderBy === 'total_score'}
                                    direction={orderBy === 'total_score' ? order : 'asc'}
                                    onClick={() => handleRequestSort('total_score')}
                                >
                                    本月總分
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center">
                                <TableSortLabel
                                    active={orderBy === 'last_month_score'}
                                    direction={orderBy === 'last_month_score' ? order : 'asc'}
                                    onClick={() => handleRequestSort('last_month_score')}
                                >
                                    上月總分
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center">
                                <TableSortLabel
                                    active={orderBy === 'score_level'}
                                    direction={orderBy === 'score_level' ? order : 'asc'}
                                    onClick={() => handleRequestSort('score_level')}
                                >
                                    評分等級
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center">
                                <TableSortLabel
                                    active={orderBy === 'over_speed_score'}
                                    direction={orderBy === 'over_speed_score' ? order : 'asc'}
                                    onClick={() => handleRequestSort('over_speed_score')}
                                >
                                    超速分數
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center">
                                <TableSortLabel
                                    active={orderBy === 'acceleration_score'}
                                    direction={orderBy === 'acceleration_score' ? order : 'asc'}
                                    onClick={() => handleRequestSort('acceleration_score')}
                                >
                                    急加速分數
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center">
                                <TableSortLabel
                                    active={orderBy === 'brake_score'}
                                    direction={orderBy === 'brake_score' ? order : 'asc'}
                                    onClick={() => handleRequestSort('brake_score')}
                                >
                                    急煞車分數
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center">
                                <TableSortLabel
                                    active={orderBy === 'turn_score'}
                                    direction={orderBy === 'turn_score' ? order : 'asc'}
                                    onClick={() => handleRequestSort('turn_score')}
                                >
                                    急轉彎分數
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedDrivers.map((driver) => (
                            <TableRow key={driver.identifier}>
                                <TableCell align="center">{driver.identifier}</TableCell>
                                <TableCell align="center">{driver.driver_name}</TableCell>
                                <TableCell align="center">{driver.total_score?.toFixed(2)}</TableCell>
                                <TableCell align="center">{driver.last_month_score?.toFixed(2)}</TableCell>
                                <TableCell align="center">{driver.score_level}</TableCell>
                                <TableCell align="center">{driver.over_speed_score?.toFixed(2)}</TableCell>
                                <TableCell align="center">{driver.acceleration_score?.toFixed(2)}</TableCell>
                                <TableCell align="center">{driver.brake_score?.toFixed(2)}</TableCell>
                                <TableCell align="center">{driver.turn_score?.toFixed(2)}</TableCell>
                                <TableCell align="center">
                                    <IconButton
                                        color="primary"
                                        onClick={() => {
                                            handleEvaluationClick(driver.identifier);
                                            setDialogOpen(true);
                                        }}
                                        title="查看駕駛評鑑"  // 新增提示文字
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                            }
                                        }}
                                    >
                                        <VisibilityIcon />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                ml: 1,
                                                display: { xs: 'none', sm: 'inline' }  // 響應式顯示
                                            }}
                                        >
                                            查看評鑑
                                        </Typography>
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={filteredDrivers.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10]}
                labelDisplayedRows={({ from, to, count }) =>
                    `第 ${from}-${to} 筆，共 ${count} 筆`
                }
            />

            <DriverEvaluationDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                vehicleId={selectedVehicleId}
                year={currentYear}
                month={currentMonth}
            />
        </Paper>

    );
};

DriversTable.propTypes = {
    drivers: PropTypes.arrayOf(
        PropTypes.shape({
            identifier: PropTypes.string.isRequired,
            driver_name: PropTypes.string,
            total_score: PropTypes.number,
            last_month_score: PropTypes.number,
            score_level: PropTypes.string,
            over_speed_score: PropTypes.number,
            acceleration_score: PropTypes.number,
            brake_score: PropTypes.number,
            turn_score: PropTypes.number
        })
    ).isRequired
};

export default DriversTable;